import React, { useId, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';

/*
 * Giant outlined wordmark: the outline draws itself in, then a brand gradient follows
 * the pointer through the letters. Adapted from Aceternity UI "Text Hover Effect" via
 * 21st.dev: https://21st.dev/manuarora700/text-hover-effect — unique SVG ids (two on a
 * page no longer collide), brand colours, display font, viewBox sized for the text.
 */
const TextHoverEffect = ({ text, viewBox = '0 0 1000 150', fontSize = 128, className }) => {
  const uid = useId().replace(/:/g, '');
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const [hovered, setHovered] = useState(false);
  const [mask, setMask] = useState({ cx: '50%', cy: '50%' });

  const onMove = e => {
    const rect = ref.current.getBoundingClientRect();
    setMask({
      cx: `${((e.clientX - rect.left) / rect.width) * 100}%`,
      cy: `${((e.clientY - rect.top) / rect.height) * 100}%`
    });
  };

  const textProps = {
    x: '50%',
    y: '54%',
    textAnchor: 'middle',
    dominantBaseline: 'middle',
    strokeWidth: 0.6,
    fontSize,
    fill: 'transparent',
    style: { fontFamily: 'inherit', fontWeight: 640, fontStretch: '118%', letterSpacing: '-0.02em' }
  };

  return (
    <svg
      ref={ref}
      width="100%"
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onPointerMove={onMove}
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`grad-${uid}`} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="hsl(var(--brand))" />
          <stop offset="50%" stopColor="hsl(var(--brand-2))" />
          <stop offset="100%" stopColor="hsl(var(--brand))" />
        </linearGradient>
        <motion.radialGradient
          id={`reveal-${uid}`}
          gradientUnits="userSpaceOnUse"
          r="22%"
          initial={{ cx: '50%', cy: '50%' }}
          animate={mask}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <stop offset="0%" stopColor="white" />
          <stop offset="100%" stopColor="black" />
        </motion.radialGradient>
        <mask id={`mask-${uid}`}>
          <rect x="0" y="0" width="100%" height="100%" fill={`url(#reveal-${uid})`} />
        </mask>
      </defs>

      <motion.text
        {...textProps}
        stroke="hsl(var(--foreground) / 0.22)"
        initial={reduced ? false : { strokeDashoffset: 1600, strokeDasharray: 1600 }}
        whileInView={{ strokeDashoffset: 0, strokeDasharray: 1600 }}
        viewport={{ once: true }}
        transition={{ duration: 3.2, ease: 'easeInOut' }}
      >
        {text}
      </motion.text>
      <text
        {...textProps}
        stroke={`url(#grad-${uid})`}
        strokeWidth={1}
        mask={`url(#mask-${uid})`}
        style={{ ...textProps.style, opacity: hovered ? 1 : 0, transition: 'opacity 0.4s' }}
      >
        {text}
      </text>
    </svg>
  );
};

export default TextHoverEffect;
